import { useRootStore } from 'stores/RootStore'
import { UseInfiniteQueryOptions, useInfiniteQuery } from 'react-query'
import { Response } from 'request-dot-js/index'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { reportError } from 'utils/error'
import { getNextPageParam } from 'utils/getNextPageParam'
import qs from 'qs'
import { VisualElement } from 'api/clients/VisualElementClient'

interface Props extends Filters {
  items?: number
  additional_seed?: string
}

interface Filters {
  width?: number
  height?: number
  source_id?: number
  element_type?: 'Template' | 'Element'
  category?: string[]
  type?: string[]
  style?: string[]
  order_by: 'random' | 'popular'
  query?: string
  basic_layout?: boolean
  has_replaceable?: boolean
  replaceable_has_background?: boolean
  template_binder_id?: number
}

type Data = { visual_elements: VisualElement[] }

export const fetchVisualElements = async (props: Props & { page?: number }) => {
  const params = qs.stringify(props, { arrayFormat: 'brackets' })
  const res = await sendToApi<Data>(`/visual_elements?${params}`)
  if (res.type === SUCCESS_RESPONSE_TYPE) {
    return res
  }
  throw new Error()
}

export const useVisualTemplates = (
  {
    width,
    height,
    items = 9,
    element_type,
    source_id,
    category,
    type,
    style,
    order_by,
    query,
    additional_seed,
    basic_layout,
    has_replaceable,
    replaceable_has_background,
    template_binder_id,
  }: Props,
  options?: Partial<UseInfiniteQueryOptions<Response<Data>>>,
) => {
  const queryKey = [
    'visualTemplates',
    {
      width,
      height,
      source_id,
      element_type,
      category,
      type,
      style,
      order_by,
      query,
      additional_seed,
      basic_layout,
      has_replaceable,
      replaceable_has_background,
      template_binder_id,
    },
  ]
  const { visualElements } = useRootStore()

  return useInfiniteQuery<Response<Data>>(
    queryKey,
    async ({ pageParam = 1 }) => {
      const response = await fetchVisualElements({
        width,
        height,
        items,
        page: pageParam,
        source_id,
        element_type,
        category,
        type,
        style,
        order_by,
        query,
        additional_seed,
        basic_layout,
        has_replaceable,
        replaceable_has_background,
        template_binder_id,
      })
      visualElements.bulkAdd(response.data.visual_elements)
      return response
    },
    {
      ...options,
      retry: false,
      refetchOnWindowFocus: false,
      getNextPageParam,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
    },
  )
}
